/* eslint-disable max-depth */
import { useEffect } from 'react'

import { extractByTypename, isEmptyArray } from '@local/utils'

import { federationApolloClient } from '@web/libs/apollo/federationClient'
import { UseTimelineQueryFragment, useTimelineQuery } from '@web/pages/timeline/hooks/useTimelineQuery'

const CHANNEL_NAME = 'myplace_global_cache_async_channel'

export interface GlobalCacheAsyncMessage {
  type: 'timeline'
  id: string
}

export const useGlobalCacheAsync = () => {
  const { getTimeline } = useTimelineQuery()

  useEffect(() => {
    if ('BroadcastChannel' in window === false) return () => {}

    const channel = new BroadcastChannel(CHANNEL_NAME)

    const handleMessage = async (event: MessageEvent<GlobalCacheAsyncMessage>) => {
      try {
        const { id, type } = event.data

        if (type === 'timeline' && federationApolloClient) {
          const cacheData = federationApolloClient.cache.extract()

          const timelines = Object.entries(cacheData)
            .filter(([key, entry]) => entry?.id === id && key.includes('Timeline'))
            .map(([cacheId, timeline]) => ({
              cacheId,
              timeline,
            }))

          if (isEmptyArray(timelines)) return

          const { data } = await getTimeline({ variables: { id } })
          const { Timeline_Succeed, Timeline_NotFound } = extractByTypename(data?.timeline)

          for (const { cacheId } of timelines) {
            if (Timeline_Succeed) {
              federationApolloClient.writeFragment({
                id: cacheId,
                fragment: UseTimelineQueryFragment,
                fragmentName: 'UseTimelineQueryFragment_Timeline',
                data: Timeline_Succeed.timeline,
              })
            }

            if (Timeline_NotFound) {
              federationApolloClient.cache.evict({ id: cacheId })
              federationApolloClient.cache.gc()
            }
          }
        }
      } catch (error) {
        console.error('Error processing message', error)
      }
    }

    channel.addEventListener('message', handleMessage)

    return () => {
      channel.removeEventListener('message', handleMessage)
      channel.close()
    }
  }, [])
}

export const sendCacheAsyncMessage = (message: GlobalCacheAsyncMessage) => {
  if ('BroadcastChannel' in window === false) return

  const channel = new BroadcastChannel(CHANNEL_NAME)
  channel.postMessage(message)
  channel.close()
}
